<script setup lang="ts">
import {
  Badge,
  BadgeColor,
  Button,
  ButtonSize,
  ButtonVariant,
  Card,
  CardActions,
  CardAspect,
  CardEyebrow,
  CardMedia,
  CardMediaChip,
  CardSubtitle,
  CardTitle,
  IconName,
  Tooltip,
  Icon,
} from '@exfluential/ui';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import StatusBadge from '~/components/learning/StatusBadge.vue';
import ProgressBar from '~/components/learning/ProgressBar.vue';
import { getModuleCtaLabel } from '~/components/learning/module-cta';
import { UserProgressStatus } from '~/api/learning';
import { UserDashboardModuleDto } from '~/api/dashboard';

type Props = {
  module: UserDashboardModuleDto;
};

const props = defineProps<Props>();

const { t } = useI18n();

const moduleUrl = computed(() =>
  props.module.curriculumId
    ? `/learning/${props.module.curriculumId}/modules/${props.module.slug}`
    : `/learning/modules/${props.module.slug}`,
);

const status = computed(
  () => props.module.progress?.userStatus as UserProgressStatus | undefined,
);

const ctaLabel = computed(() =>
  getModuleCtaLabel(t, status.value, props.module.progress?.canRetake),
);

const isLocked = computed(() => props.module.isLocked === true);

const COMPLETED_STATUSES = new Set<UserProgressStatus>([
  UserProgressStatus.COMPLETED,
  UserProgressStatus.PASSED,
  UserProgressStatus.AWAITING_REVIEW,
]);

const ctaVariant = computed(() =>
  status.value !== undefined && COMPLETED_STATUSES.has(status.value)
    ? ButtonVariant.OUTLINED
    : ButtonVariant.FILLED,
);

// The design keeps the progress track and status line ("Let's get started!",
// "Keep going") visible in every state, with the percentage only once started.
const showProgressPercent = computed(
  () =>
    status.value !== undefined &&
    status.value !== UserProgressStatus.NOT_STARTED,
);

const titleTarget = computed(() =>
  isLocked.value ? undefined : moduleUrl.value,
);
</script>

<template>
  <Tooltip
    :text="$t('learning.sequentialModuleLockedTooltip')"
    :disabled="!isLocked"
  >
    <Card
      :interactive="!isLocked"
      :class="isLocked ? 'opacity-55 grayscale-[0.4] cursor-not-allowed' : ''"
    >
      <CardMedia
        :src="module.imageUrl"
        :to="titleTarget"
        :alt="module.title"
        :aspect="CardAspect.RATIO_16_9"
        :fallback-icon-name="IconName.FILE_IMAGE"
      >
        <template #top-left>
          <CardMediaChip v-if="isLocked" :label="$t('learning.locked')">
            <Icon :name="IconName.LOCK" class="size-[0.75rem]" />
          </CardMediaChip>
        </template>
        <template #top-right>
          <StatusBadge v-if="status" :status="status" />
        </template>
      </CardMedia>

      <CardEyebrow :label="$t('search.type-label.module')" />

      <CardTitle :title="module.title" :to="titleTarget" />

      <CardSubtitle v-if="module.progress">
        <i18n-t keypath="learning.activitiesCompleted" tag="span">
          <template #completed>
            <span class="text-primary">
              {{ module.progress.completedActivitiesCount }}
            </span>
          </template>
          <template #total>
            <span class="text-primary">
              {{ module.progress.activitiesCount }}
            </span>
          </template>
        </i18n-t>
      </CardSubtitle>

      <ProgressBar
        v-if="module.progress"
        :progress-percent="module.progress.progressPercent"
        :status="status"
        :completed-activities="module.progress.completedActivitiesCount"
        :total-activities="module.progress.activitiesCount"
        :can-retake="module.progress.canRetake"
        :show-label="showProgressPercent"
        status-message-on
      />

      <CardActions>
        <template #start>
          <Button
            :label="ctaLabel"
            :to="titleTarget"
            :disabled="isLocked"
            :size="ButtonSize.SM"
            :variant="ctaVariant"
          />
        </template>
      </CardActions>
    </Card>
  </Tooltip>
</template>
